"use client";

import { css, cx } from "styled-system/css";
import { format } from "date-fns";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import { es } from "date-fns/locale";
import { overlapConfirmMessage, type ConflictInfo } from "./overbook";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import type { AppointmentStatus, PaymentStatus } from "@prisma/client";
import { labelAppointmentStatus, labelPaymentStatus } from "@/lib/dashboard-labels";
import { WhatsAppChatLink, CopyPhoneButton } from "@/components/dashboard/whatsapp-chat-link";
import { useConfirm } from "@/hooks/useConfirm";
import { useToast } from "@/hooks/useToast";
import {
  CheckCircle2,
  CalendarClock,
  CheckCheck,
  UserX,
  Ban,
  Trash2,
  Pencil,
} from "lucide-react";

export type DrawerAppointment = {
  id: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string | null;
  serviceId: string | null;
  courtId?: string | null;
  serviceName: string;
  servicePrice: number | null;
  status: string;
  paymentStatus: string;
  depositStatus: string | null;
  depositAmount: number | null;
  depositPaidAt: string | null;
  amountPaid: number;
  reminderSent24?: boolean;
  reminderSent1h?: boolean;
  whatsappUnavailable?: boolean;
  notes?: string | null;
  /** Título original del evento de Google, capturado al convertirlo en turno (ver GoogleEventDrawer). */
  originalGoogleTitle?: string | null;
  startTime: string;
  endTime: string;
  staffMemberId?: string | null;
  staffMemberName?: string | null;
};

type PaymentRow = {
  id: string;
  amount: number;
  method: string;
  paidAt: string;
  note: string | null;
};

function initials(name: string) {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.slice(0, 1).toUpperCase()).join("");
}

function badgeTone(status: string) {
  if (status === "COMPLETED") return { bg: "#ecfdf5", color: "#166534", border: "#bbf7d0" };
  if (status === "NO_SHOW") return { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" };
  if (status === "CANCELLED") return { bg: "#f1f5f9", color: "#64748b", border: "#e2e8f0" };
  if (status === "CONFIRMED") return { bg: "#eef2ff", color: "#3730a3", border: "#c7d2fe" };
  return { bg: "#fffbeb", color: "#b45309", border: "#fcd34d" };
}

function paymentBadgeTone(status: string) {
  if (status === "PAID") return { bg: "#ecfdf5", color: "#166534", border: "#bbf7d0" };
  if (status === "UNPAID") return { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" };
  if (status === "REFUNDED") return { bg: "#f1f5f9", color: "#64748b", border: "#e2e8f0" };
  return { bg: "#fffbeb", color: "#b45309", border: "#fcd34d" }; // DEPOSIT_PAID / PARTIAL_PAID
}

const cardStyle = css({
  rounded: "xl",
  borderWidth: "1px",
  borderColor: "#e2e8f0",
  bg: "white",
  p: "4",
  boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
});

const actionTones = {
  confirm: { bg: "#ecfdf5", border: "#a7f3d0", color: "#047857", hoverBg: "#d1fae5" },
  reschedule: { bg: "#eef2ff", border: "#c7d2fe", color: "#4338ca", hoverBg: "#e0e7ff" },
  complete: { bg: "#f1f5f9", border: "#cbd5e1", color: "#334155", hoverBg: "#e2e8f0" },
  noShow: { bg: "#fffbeb", border: "#fde68a", color: "#b45309", hoverBg: "#fef3c7" },
  cancel: { bg: "#fff5f5", border: "#fecaca", color: "#b91c1c", hoverBg: "#fee2e2" },
} as const;

function ActionButton({
  tone,
  icon,
  label,
  disabled,
  onClick,
  fullWidth,
}: {
  tone: keyof typeof actionTones;
  icon: ReactNode;
  label: string;
  disabled?: boolean;
  onClick: () => void;
  fullWidth?: boolean;
}) {
  const t = actionTones[tone];
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={css({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "2",
        w: fullWidth ? "100%" : undefined,
        py: "2.5",
        px: "3",
        rounded: "lg",
        borderWidth: "1px",
        fontSize: "sm",
        fontWeight: "semibold",
        cursor: "pointer",
        transition: "transform 160ms cubic-bezier(0.23, 1, 0.32, 1), opacity 150ms ease",
        _active: { transform: "scale(0.97)" },
        _disabled: { opacity: 0.45, cursor: "not-allowed" },
      })}
      style={{ backgroundColor: t.bg, borderColor: t.border, color: t.color }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.backgroundColor = t.hoverBg; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = t.bg; }}
    >
      {icon}
      {label}
    </button>
  );
}

function methodLabel(m: string) {
  if (m === "CASH") return "Efectivo";
  if (m === "TRANSFER") return "Transferencia";
  if (m === "MERCADOPAGO") return "Mercado Pago";
  if (m === "OTHER") return "Otro";
  return m;
}

type NotifLogRow = {
  channel: string;
  type: string;
  status: string;
  error: string | null;
  sentAt: string;
};

const NOTIF_TYPE_LABEL: Record<string, string> = {
  confirmation: "Confirmación al cliente",
  cancellation: "Cancelación al cliente",
  updated: "Cambio de turno (WhatsApp)",
  deposit_pending: "Seña pendiente",
  remaining_balance: "Saldo pendiente",
  reminder_24h: "Recordatorio 24 h",
  reminder_1h: "Recordatorio 1 h",
  new_booking_pro: "Aviso: nueva reserva (tu correo)",
  deposit_paid_pro: "Aviso: seña pagada (tu correo)",
};

const NOTIF_CHANNEL_LABEL: Record<string, string> = {
  email: "Email",
  whatsapp: "WhatsApp",
};

export function AppointmentDrawer({
  open,
  appt,
  timeZone,
  onClose,
  onAction,
  onAfterPayment,
  onEditRequest,
  onDeleted,
}: {
  open: boolean;
  appt: DrawerAppointment | null;
  /** Zona horaria IANA del profesional. Las horas se muestran/reenvían en esta zona, no la del navegador. */
  timeZone?: string;
  onClose: () => void;
  onAction: (next: { status?: string }) => Promise<void>;
  onAfterPayment?: () => void | Promise<void>;
  onEditRequest?: (appt: DrawerAppointment) => void;
  onDeleted?: () => void | Promise<void>;
}) {
  const { refresh } = useAuth();
  const confirm = useConfirm();
  const toast = useToast();
  const tz = timeZone ?? "America/Argentina/Buenos_Aires";
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);
  const [payments, setPayments] = useState<PaymentRow[] | null>(null);
  const [payLoading, setPayLoading] = useState(false);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<"CASH" | "TRANSFER" | "OTHER">("CASH");
  const [note, setNote] = useState("");
  const [paidAt, setPaidAt] = useState("");
  const [payErr, setPayErr] = useState<string | null>(null);
  const [paySaving, setPaySaving] = useState(false);
  const [notifLogs, setNotifLogs] = useState<NotifLogRow[] | null>(null);
  const [rescheduleMode, setRescheduleMode] = useState(false);
  const [rescheduleVal, setRescheduleVal] = useState("");
  const [rescheduleSaving, setRescheduleSaving] = useState(false);
  const [staffList, setStaffList] = useState<{ id: string; name: string; googleColorId: string; googleCalendarBgColor: string | null; googleCalendarId: string | null }[]>([]);
  const [staffDraft, setStaffDraft] = useState("");
  const [staffSaving, setStaffSaving] = useState(false);

  const loadPayments = useCallback(async () => {
    if (!appt?.id) return;
    setPayLoading(true);
    const r = await fetchWithSessionSync(
      `/api/dashboard/appointments/${encodeURIComponent(appt.id)}/payments`,
      { credentials: "include" },
      refresh,
    );
    if (r.ok) {
      const j = (await r.json()) as { payments: PaymentRow[] };
      setPayments(j.payments);
    } else {
      setPayments([]);
    }
    setPayLoading(false);
  }, [appt?.id, refresh]);

  const loadNotifLogs = useCallback(async () => {
    if (!appt?.id) return;
    const r = await fetchWithSessionSync(
      `/api/dashboard/appointments/${encodeURIComponent(appt.id)}/notif-log`,
      { credentials: "include" },
      refresh,
    );
    if (r.ok) {
      const j = (await r.json()) as { logs: NotifLogRow[] };
      setNotifLogs(j.logs);
    } else {
      setNotifLogs([]);
    }
  }, [appt?.id, refresh]);

  useEffect(() => {
    if (!open || !appt) {
      setPayments(null);
      setNotifLogs(null);
      setRescheduleMode(false);
      setRescheduleVal("");
      setAmount("");
      setNote("");
      setPaidAt("");
      setPayErr(null);
      return;
    }
    setStaffDraft(appt.staffMemberId ?? "");
    void loadPayments();
    void loadNotifLogs();
    void (async () => {
      const r = await fetchWithSessionSync("/api/dashboard/agenda-staff", { credentials: "include" }, refresh);
      if (!r.ok) {
        setStaffList([]);
        return;
      }
      const d = (await r.json()) as { staff: { id: string; name: string; googleColorId: string; isActive: boolean; googleCalendarBgColor: string | null; googleCalendarId: string | null }[] };
      setStaffList(d.staff.filter((s) => s.isActive));
    })();
  }, [open, appt, loadPayments, loadNotifLogs, refresh]);

  if (!open || !appt) return null;
  const tone = badgeTone(appt.status);
  const canPay = appt.status !== "CANCELLED";

  async function submitPayment() {
    if (!appt) return;
    const n = Number(String(amount).replace(",", "."));
    if (!Number.isFinite(n) || n <= 0) {
      setPayErr("Importe inválido");
      return;
    }
    setPayErr(null);
    setPaySaving(true);
    try {
      const body: Record<string, unknown> = { amount: n, method, note: note.trim() || null };
      if (paidAt.trim()) {
        const d = new Date(paidAt);
        if (!Number.isNaN(d.getTime())) body.paidAt = d.toISOString();
      }
      const r = await fetchWithSessionSync(
        `/api/dashboard/appointments/${encodeURIComponent(appt.id)}/payments`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
        refresh,
      );
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        setPayErr(j.error ?? "Error al registrar");
        return;
      }
      setAmount("");
      setNote("");
      setPaidAt("");
      await loadPayments();
      await onAfterPayment?.();
    } finally {
      setPaySaving(false);
    }
  }

  const visibleNotifLogs = notifLogs?.filter((l) => l.channel !== "sms") ?? null;

  async function handleDelete() {
    if (!appt) return;
    const apptId = appt.id;
    const ok = await confirm({
      message: "¿Eliminás este turno? Esta acción no se puede deshacer.",
      tone: "danger",
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const r = await fetchWithSessionSync(
        `/api/dashboard/appointments/${encodeURIComponent(apptId)}`,
        { method: "DELETE" },
        refresh,
      );
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        toast.error(j.error ?? "No se pudo eliminar el turno");
        return;
      }
      const j = (await r.json().catch(() => ({}))) as { refundMessage?: string };
      if (j.refundMessage) toast.info(j.refundMessage);
      onClose();
      await onDeleted?.();
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div
      className={css({
        position: "fixed",
        inset: 0,
        zIndex: 60,
        bg: "rgba(0,0,0,0.35)",
        display: "flex",
        justifyContent: "flex-end",
      })}
      onClick={(e) => e.currentTarget === e.target && onClose()}
    >
      <aside
        className={css({
          width: { base: "100%", sm: "420px" },
          height: "100%",
          maxHeight: "100dvh",
          bg: "white",
          borderLeftWidth: "1px",
          borderColor: "#e2e8f0",
          display: "flex",
          flexDir: "column",
          overflow: "hidden",
        })}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={css({
            flexShrink: 0,
            px: "5",
            pt: "5",
            pb: "4",
            borderBottomWidth: "1px",
            borderColor: "#f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "3",
          })}
        >
          <div className={css({ display: "flex", gap: "3", alignItems: "center" })}>
            <div
              className={css({
                w: "10",
                h: "10",
                rounded: "full",
                bg: "#eef2ff",
                color: "#3730a3",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "bold",
              })}
            >
              {initials(appt.clientName)}
            </div>
            <div className={css({ minW: 0 })}>
              <p className={css({ fontWeight: "bold", color: "#0f172a", fontSize: "sm", lineHeight: "1.3" })}>{appt.clientName}</p>
              <p className={css({ fontSize: "xs", fontWeight: "normal", color: "#94a3b8", lineHeight: "1.4", mt: "0.5" })}>
                {[appt.clientEmail, appt.clientPhone].filter(Boolean).join(" · ")}
              </p>
              {appt.clientEmail ? (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    navigate(`/dashboard/odontogram/by-email/${encodeURIComponent(appt.clientEmail)}`);
                  }}
                  className={css({
                    fontSize: "xs",
                    fontWeight: "semibold",
                    color: "token(colors.brand.700)",
                    bg: "rgba(99,102,241,0.08)",
                    px: "2",
                    py: "0.5",
                    rounded: "md",
                    border: "none",
                    cursor: "pointer",
                    mt: "1.5",
                    display: "inline-block",
                    _hover: { bg: "rgba(99,102,241,0.16)" },
                  })}
                >
                  Ver historia clínica
                </button>
              ) : null}
            </div>
          </div>
          <div className={css({ display: "flex", gap: "2", alignItems: "center" })}>
            <WhatsAppChatLink phone={appt.clientPhone} variant="icon" />
            <CopyPhoneButton phone={appt.clientPhone} />
            <button
              type="button"
              onClick={onClose}
              className={css({ border: "none", bg: "transparent", cursor: "pointer", fontSize: "20px", color: "#64748b" })}
              aria-label="Cerrar"
            >
              ×
            </button>
          </div>
        </div>

        <div className={css({ flex: "1", minH: "0", overflowY: "auto", px: "5", py: "4" })}>
          <div className={css({ display: "flex", flexDir: "column", gap: "4" })}>
          {appt.whatsappUnavailable ? (
            <div
              className={css({
                rounded: "lg",
                borderWidth: "1px",
                borderColor: "#fecaca",
                bg: "#fef2f2",
                p: "3",
                display: "flex",
                flexDir: "column",
                gap: "0.5",
              })}
            >
              <p className={css({ fontSize: "xs", fontWeight: "semibold", color: "#dc2626" })}>
                ⚠️ Sin WhatsApp
              </p>
              <p className={css({ fontSize: "xs", color: "#b91c1c", lineHeight: "1.5" })}>
                Un envío de WhatsApp a este número falló porque no tiene WhatsApp. No se van a reintentar envíos por este canal a este teléfono.
              </p>
            </div>
          ) : null}
          <div className={cx(cardStyle, css({ display: "flex", flexDir: "column", gap: "3" }))}>
          <div className={css({ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "3" })}>
            <span className={css({ fontSize: "sm", color: "#64748b", flexShrink: 0 })}>Servicio</span>
            <span className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a", textAlign: "right", minW: 0, wordBreak: "break-word" })}>{appt.serviceName}</span>
          </div>
          <div className={css({ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "3" })}>
            <span className={css({ fontSize: "sm", color: "#64748b", flexShrink: 0 })}>Horario</span>
            <span className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a", textAlign: "right", minW: 0 })}>
              {formatInTimeZone(new Date(appt.startTime), tz, "dd/MM/yyyy HH:mm")}
            </span>
          </div>

          {appt.originalGoogleTitle ? (
            <div className={css({ rounded: "lg", borderWidth: "1px", borderColor: "#fde68a", bg: "#fffbeb", p: "3" })}>
              <p className={css({ fontSize: "xs", fontWeight: "semibold", color: "#b45309", mb: "1" })}>
                Título original en Google Calendar
              </p>
              <p className={css({ fontSize: "sm", color: "#78350f", wordBreak: "break-word" })}>
                {appt.originalGoogleTitle}
              </p>
            </div>
          ) : null}

          {staffList.length > 0 ? (
            <div className={css({ display: "flex", flexDir: "column", gap: "1" })}>
              <span className={css({ fontSize: "sm", color: "#64748b" })}>Profesional</span>
              <div
                className={css({
                  display: "flex",
                  alignItems: "center",
                  gap: "2",
                  px: "3",
                  py: "2",
                  rounded: "md",
                  borderWidth: "1px",
                  borderColor: "#e2e8f0",
                  bg: "white",
                  _focusWithin: { borderColor: "token(colors.brand.400)" },
                })}
              >
                {(() => {
                  const sel = staffList.find((s) => s.id === staffDraft);
                  const dot = sel?.googleCalendarBgColor ?? null;
                  return (
                    <span
                      className={css({ w: "2.5", h: "2.5", rounded: "full", flexShrink: 0, borderWidth: dot ? "1px" : "0", borderColor: "rgba(0,0,0,0.1)" })}
                      style={{ backgroundColor: dot ?? "#cbd5e1" }}
                    />
                  );
                })()}
                <select
                  className={css({
                    flex: "1",
                    minW: 0,
                    border: "none",
                    outline: "none",
                    bg: "transparent",
                    fontSize: "sm",
                    color: "#0f172a",
                    cursor: "pointer",
                  })}
                  value={staffDraft}
                  disabled={staffSaving || appt.status === "CANCELLED"}
                  onChange={(e) => {
                    const v = e.target.value;
                    setStaffDraft(v);
                    void (async () => {
                      setStaffSaving(true);
                      const r = await fetchWithSessionSync(
                        `/api/dashboard/appointments/${encodeURIComponent(appt.id)}`,
                        {
                          method: "PATCH",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ staffMemberId: v || null }),
                        },
                        refresh,
                      );
                      setStaffSaving(false);
                      if (r.ok) await onAfterPayment?.();
                    })();
                  }}
                >
                  <option value="">Sin asignar</option>
                  {staffList.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              {staffDraft && staffList.find((s) => s.id === staffDraft)?.googleCalendarId ? (
                <span className={css({ fontSize: "xs", color: "#64748b" })}>
                  Turno en agenda "{staffList.find((s) => s.id === staffDraft)?.name}" de Google
                </span>
              ) : appt.staffMemberName && !staffDraft ? (
                <span className={css({ fontSize: "xs", color: "#64748b" })}>Actual: {appt.staffMemberName}</span>
              ) : null}
            </div>
          ) : null}

          <div className={css({ display: "flex", alignItems: "center", justifyContent: "space-between" })}>
            <span className={css({ fontSize: "sm", color: "#64748b" })}>Estado</span>
            <span
              className={css({
                fontSize: "xs",
                fontWeight: "bold",
                px: "3",
                py: "1.5",
                rounded: "999px",
                borderWidth: "1px",
                borderColor: tone.border,
                bg: tone.bg,
                color: tone.color,
              })}
            >
              {labelAppointmentStatus(appt.status as AppointmentStatus)}
            </span>
          </div>
          </div>

          <div className={cardStyle}>
            <div className={css({ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "3" })}>
              <p className={css({ fontSize: "xs", fontWeight: "semibold", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.03em" })}>
                Cobros (ARS)
              </p>
              {(() => {
                const pTone = paymentBadgeTone(appt.paymentStatus);
                return (
                  <span
                    className={css({
                      fontSize: "xs",
                      fontWeight: "bold",
                      px: "3",
                      py: "1",
                      rounded: "999px",
                      borderWidth: "1px",
                      borderColor: pTone.border,
                      bg: pTone.bg,
                      color: pTone.color,
                    })}
                  >
                    {labelPaymentStatus(appt.paymentStatus as PaymentStatus)}
                  </span>
                );
              })()}
            </div>
            <p className={css({ fontSize: "2xl", fontWeight: "bold", color: "#0f172a", lineHeight: "1.2" })}>
              ${Math.round(appt.amountPaid)}
            </p>
            <p className={css({ fontSize: "xs", color: "#94a3b8", mt: "1" })}>
              Registrado en tabla
              {appt.servicePrice != null ? ` · Precio servicio: $${Math.round(appt.servicePrice)}` : null}
            </p>

            {appt.depositStatus ? (
              <div className={css({ mt: "3", pt: "3", borderTopWidth: "1px", borderColor: "#f1f5f9" })}>
                <p className={css({ fontSize: "xs", color: "#64748b", mb: "1" })}>Seña</p>
                <p className={css({ fontSize: "sm", color: "#0f172a" })}>
                  <strong>{labelPaymentStatus(appt.depositStatus as PaymentStatus)}</strong>
                  {appt.depositAmount != null ? ` · $${appt.depositAmount}` : ""}
                </p>
                {appt.depositPaidAt ? (
                  <p className={css({ fontSize: "xs", color: "#64748b", mt: "1" })}>
                    Pagada el {new Date(appt.depositPaidAt).toLocaleString("es-AR")}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className={cardStyle}>
            <p className={css({ fontSize: "xs", fontWeight: "semibold", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.03em", mb: "3" })}>
              Historial de cobros
            </p>
            {payLoading || payments === null ? (
              <p className={css({ fontSize: "sm", color: "#94a3b8" })}>Cargando…</p>
            ) : payments.length === 0 ? (
              <p className={css({ fontSize: "sm", color: "#94a3b8", textAlign: "center", py: "3" })}>Sin cobros registrados</p>
            ) : (
              <ul className={css({ listStyle: "none", m: 0, p: 0, display: "flex", flexDir: "column", gap: "2" })}>
                {payments.map((p) => (
                  <li key={p.id} className={css({ fontSize: "xs", color: "#334155", borderBottomWidth: "1px", borderColor: "#f1f5f9", pb: "2" })}>
                    <span className={css({ fontWeight: "bold", fontSize: "sm", color: "#0f172a" })}>${p.amount}</span>
                    <span className={css({ color: "#64748b", ml: "2" })}>{methodLabel(p.method)}</span>
                    <span className={css({ display: "block", color: "#94a3b8", mt: "0.5" })}>
                      {format(new Date(p.paidAt), "d MMM yyyy HH:mm", { locale: es })}
                      {p.note ? ` · ${p.note}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {canPay ? (
            <div className={css({ rounded: "xl", borderWidth: "1px", borderColor: "#c7d2fe", bg: "#eef2ff", p: "4", boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)" })}>
              <p className={css({ fontSize: "xs", fontWeight: "semibold", color: "#3730a3", mb: "3" })}>Registrar cobro manual</p>
              {payErr ? <p className={css({ fontSize: "xs", color: "#b91c1c", mb: "2" })}>{payErr}</p> : null}
              <div className={css({ display: "flex", flexDir: "column", gap: "2" })}>
                <Input placeholder="Importe ARS" value={amount} onChange={(e) => setAmount(e.target.value)} />
                <select
                  value={method}
                  onChange={(e) => setMethod(e.target.value as "CASH" | "TRANSFER" | "OTHER")}
                  className={css({
                    py: "2",
                    px: "3",
                    rounded: "lg",
                    borderWidth: "1px",
                    borderColor: "#e2e8f0",
                    fontSize: "sm",
                  })}
                >
                  <option value="CASH">Efectivo</option>
                  <option value="TRANSFER">Transferencia</option>
                  <option value="OTHER">Otro</option>
                </select>
                <Input placeholder="Nota (opcional)" value={note} onChange={(e) => setNote(e.target.value)} />
                <label className={css({ fontSize: "xs", color: "#64748b" })}>
                  Fecha del cobro (opcional)
                  <Input type="datetime-local" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} className={css({ mt: "1" })} />
                </label>
                <Button type="button" disabled={paySaving} onClick={() => void submitPayment()}>
                  {paySaving ? "Guardando…" : "Registrar cobro"}
                </Button>
              </div>
            </div>
          ) : null}

          <div className={cardStyle}>
            <p className={css({ fontSize: "xs", fontWeight: "semibold", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.03em", mb: "3" })}>
              Notificaciones enviadas
            </p>
            {notifLogs === null ? (
              <p className={css({ fontSize: "xs", color: "#94a3b8" })}>Cargando…</p>
            ) : !visibleNotifLogs || visibleNotifLogs.length === 0 ? (
              <p className={css({ fontSize: "xs", color: "#94a3b8" })}>Sin notificaciones registradas.</p>
            ) : (
              <div className={css({ display: "flex", flexDir: "column", gap: "2" })}>
                {visibleNotifLogs.map((log, i) => (
                  <div key={i} className={css({ display: "flex", flexDir: "column", gap: "1", py: "1", borderBottomWidth: i < visibleNotifLogs.length - 1 ? "1px" : "0", borderColor: "#f1f5f9" })}>
                    <div className={css({ display: "flex", alignItems: "flex-start", gap: "2" })}>
                      <span
                        className={css({
                          fontSize: "10px",
                          w: "3.5",
                          h: "3.5",
                          rounded: "full",
                          flexShrink: 0,
                          mt: "0.5",
                          bg: log.status === "sent" ? "#22c55e" : "#f87171",
                          display: "inline-block",
                        })}
                      />
                      <span className={css({ fontSize: "xs", color: "#334155", flex: 1, lineHeight: "1.45" })}>
                        <span className={css({ fontWeight: "medium" })}>
                          {NOTIF_TYPE_LABEL[log.type] ?? log.type.replace(/_/g, " ")}
                        </span>{" "}
                        <span className={css({ color: "#64748b" })}>
                          · {NOTIF_CHANNEL_LABEL[log.channel] ?? log.channel}
                        </span>
                      </span>
                    </div>
                    {log.status !== "sent" && log.error ? (
                      <p className={css({ fontSize: "10px", color: "#dc2626", m: 0, pl: "6", lineHeight: "1.4", wordBreak: "break-word" })}>{log.error}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
          </div>
        </div>

        <div className={css({ flexShrink: 0, px: "5", pb: "5", pt: "4", borderTopWidth: "1px", borderColor: "#f1f5f9", bg: "#fafafa" })}>
        {/* Reagendar inline */}
        {rescheduleMode ? (
          <div className={css({ p: "4", rounded: "xl", borderWidth: "1px", borderColor: "#c7d2fe", bg: "#eef2ff" })}>
            <p className={css({ fontSize: "xs", fontWeight: "semibold", color: "#3730a3", mb: "2" })}>Nueva fecha y hora</p>
            <input
              type="datetime-local"
              value={rescheduleVal}
              onChange={(e) => setRescheduleVal(e.target.value)}
              className={css({ w: "100%", px: "3", py: "2", rounded: "lg", borderWidth: "1px", borderColor: "#c7d2fe", bg: "white", fontSize: "sm", mb: "3", _focus: { outline: "none", borderColor: "#6366f1" } })}
            />
            <div className={css({ display: "flex", gap: "2" })}>
              <Button
                variant="primary"
                disabled={!rescheduleVal || rescheduleSaving}
                onClick={() => {
                  void (async () => {
                    setRescheduleSaving(true);
                    // Interpretamos la hora ingresada en la zona del profesional (no la del navegador).
                    const startIso = fromZonedTime(rescheduleVal, tz).toISOString();
                    const patch = (allowOverlap: boolean) =>
                      fetchWithSessionSync(
                        `/api/dashboard/appointments/${appt.id}`,
                        {
                          method: "PATCH",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ startTimeIso: startIso, force: true, allowOverlap }),
                        },
                        refresh,
                      );
                    let r = await patch(false);
                    if (!r.ok) {
                      const j = await r.json().catch(() => ({})) as { error?: string; code?: string; conflict?: ConflictInfo };
                      if (j.code === "OVERLAP" && j.conflict && (await confirm(overlapConfirmMessage(j.conflict, tz)))) {
                        r = await patch(true);
                      } else {
                        setRescheduleSaving(false);
                        toast.error(j.error ?? "No se pudo reagendar");
                        return;
                      }
                    }
                    setRescheduleSaving(false);
                    if (r.ok) {
                      setRescheduleMode(false);
                      await onAction({});
                    } else {
                      const j = await r.json().catch(() => ({})) as { error?: string };
                      toast.error(j.error ?? "No se pudo reagendar el sobreturno");
                    }
                  })();
                }}
              >
                {rescheduleSaving ? "Guardando…" : "Confirmar reagendado"}
              </Button>
              <Button variant="secondary" onClick={() => setRescheduleMode(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <>
          {/* Editar / Eliminar */}
          <div className={css({ display: "flex", gap: "2", mb: "3" })}>
            <button
              type="button"
              disabled={appt.status === "CANCELLED"}
              onClick={() => { onEditRequest?.(appt); onClose(); }}
              className={css({
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "2",
                flex: "1",
                py: "2",
                px: "3",
                rounded: "lg",
                borderWidth: "1px",
                borderColor: "#c7d2fe",
                bg: "#eef2ff",
                color: "#4338ca",
                fontSize: "sm",
                fontWeight: "medium",
                cursor: "pointer",
                _disabled: { opacity: "0.5", cursor: "not-allowed" },
                _hover: { bg: "#e0e7ff" },
              })}
            >
              <Pencil size={14} />
              Editar turno
            </button>
            <button
              type="button"
              disabled={deleting}
              onClick={() => void handleDelete()}
              className={css({
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "2",
                flex: "1",
                py: "2",
                px: "3",
                rounded: "lg",
                borderWidth: "1px",
                borderColor: "#fecaca",
                bg: "#fff5f5",
                color: "#b91c1c",
                fontSize: "sm",
                fontWeight: "medium",
                cursor: "pointer",
                _disabled: { opacity: "0.5", cursor: "not-allowed" },
                _hover: { bg: "#fee2e2" },
              })}
            >
              <Trash2 size={14} />
              {deleting ? "Eliminando..." : "Eliminar"}
            </button>
          </div>

          <div className={css({ display: "grid", gridTemplateColumns: { base: "1fr 1fr", sm: "1fr 1fr" }, gap: "2" })}>
            {/* Confirmar — si ya se envió confirmación, pregunta antes */}
            <ActionButton
              tone="confirm"
              icon={<CheckCircle2 size={16} />}
              label="Confirmar"
              disabled={appt.status === "CANCELLED"}
              onClick={() => {
                void (async () => {
                  const alreadySent = notifLogs?.some((l) => l.type === "confirmation" && l.status === "sent");
                  if (alreadySent) {
                    const ok = await confirm("Ya se envió una confirmación a este cliente. ¿Querés enviar de nuevo la confirmación?");
                    if (!ok) return;
                  }
                  await onAction({ status: "CONFIRMED" });
                })();
              }}
            />

            {/* Reagendar */}
            <ActionButton
              tone="reschedule"
              icon={<CalendarClock size={16} />}
              label="Reagendar"
              disabled={appt.status === "CANCELLED"}
              onClick={() => {
                void (async () => {
                  const alreadySent = notifLogs?.some((l) => l.type === "confirmation" && l.status === "sent");
                  // Inicializamos el input datetime-local con la hora del turno en la zona del profesional.
                  const start = appt.startTime ? new Date(appt.startTime) : new Date();
                  const local = formatInTimeZone(start, tz, "yyyy-MM-dd'T'HH:mm");
                  if (alreadySent) {
                    const ok = await confirm(
                      "Ya se envió una confirmación a este cliente. Al reagendar se le notificará el nuevo horario. ¿Continuás?",
                    );
                    if (!ok) return;
                  }
                  setRescheduleVal(local);
                  setRescheduleMode(true);
                })();
              }}
            />

            <ActionButton
              tone="complete"
              icon={<CheckCheck size={16} />}
              label="Completado"
              disabled={appt.status === "CANCELLED"}
              onClick={() => void onAction({ status: "COMPLETED" })}
            />

            <ActionButton
              tone="noShow"
              icon={<UserX size={16} />}
              label="No asistió"
              disabled={appt.status === "CANCELLED"}
              onClick={() => void onAction({ status: "NO_SHOW" })}
            />
          </div>

          {/* Cancelar turno — acción destructiva, separada del resto */}
          <div className={css({ mt: "5", pt: "4", borderTopWidth: "1px", borderColor: "#fecaca" })}>
            <ActionButton
              tone="cancel"
              icon={<Ban size={16} />}
              label="Cancelar turno"
              fullWidth
              disabled={appt.status === "CANCELLED"}
              onClick={() => {
                void (async () => {
                  const ok = await confirm({
                    message: "¿Estás seguro que querés cancelar el turno? Se notificará al cliente.",
                    tone: "danger",
                    confirmLabel: "Cancelar turno",
                    cancelLabel: "Volver",
                  });
                  if (!ok) return;
                  await onAction({ status: "CANCELLED" });
                })();
              }}
            />
          </div>
          </>
        )}
        </div>
      </aside>
    </div>
  );
}
